import { getSecondary } from '@brands/Utils/getPrimaryColor';
import { Box, Checkbox, Chip, FormControl, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import cn from 'classnames';
import React, { forwardRef, useEffect, useState } from 'react';
import { RegisterOptions, UseFormRegister } from 'react-hook-form';

import styles from './styles.module.scss';

interface InputProps {
  wrapperClassName?: string;
  options: { id: string; value: string | number; label: string }[];
  inputClass?: string;
  register?: UseFormRegister<any>;
  registerOptions?: RegisterOptions;
  errors?: { [key: string]: any };
  wrapperStyle?: React.CSSProperties;
  name: string;
  label?: string;
  onChange?: any;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  value?: string[];
  required?: boolean;
  hideLabel?: boolean;
  styledTag?: boolean;
  displayCheckbox?: boolean;
  withId?: boolean;
}

const MultiSelectInputMUI = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClassName,
      options,
      inputClass,
      wrapperStyle,
      name,
      register,
      registerOptions,
      errors,
      label,
      onChange,
      onBlur,
      disabled = false,
      value = [],
      required = false,
      hideLabel = false,
      styledTag = false,
      displayCheckbox = true,
      withId = false,
      ...props
    },
    ref,
  ) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
      if (errors && name) {
        const nameArray = name.split('.');
        const error = nameArray.length > 1 ? errors[nameArray[0]]?.[nameArray[1]]?.message : errors[name]?.message;
        setErrorMessage(error || '');
      } else {
        setErrorMessage('');
      }
    }, [errors, name]);

    return (
      <Box className={cn(styles.inputWrapper, wrapperClassName)} style={wrapperStyle}>
        <FormControl sx={{ border: '0', minHeight: '56px', width: '100%' }} error={!!errorMessage}>
          <Autocomplete
            multiple
            disablePortal
            disableCloseOnSelect
            options={options}
            getOptionLabel={(option) => option.label}
            fullWidth
            value={options.filter((option) => value.includes(option.id.toString()))}
            inputValue={inputValue}
            onInputChange={(_, newVal, reason) => {
              if (reason === 'input') {
                setInputValue(newVal);
              } else if (reason === 'clear') {
                setInputValue('');
              }
              // 'reset' is intentionally ignored here; onChange handles clearing after selection
            }}
            onChange={(event, newValue) => {
              setInputValue('');
              if (onChange) {
                onChange(newValue.map((item) => String(item.id)));
              }
            }}
            disabled={disabled}
            ref={ref}
            {...props}
            renderTags={(selected, getTagProps) => {
              if (styledTag) {
                return selected.map((option, index) => (
                  <Chip
                    variant="outlined"
                    color="primary"
                    label={option.label}
                    {...getTagProps({ index })}
                    className="fs-exclude"
                    key={option.id}
                    sx={{
                      borderRadius: '24px',
                      fontWeight: 500,
                      color: getSecondary(),
                      borderColor: getSecondary(),
                      '.MuiChip-deleteIcon': {
                        color: getSecondary(),
                      },
                    }}
                  />
                ));
              }

              return selected.map((option) => option.label).join(', ');
            }}
            renderOption={(optionProps, option, { selected }) => (
              <li {...optionProps} key={option.id} className="fs-exclude">
                {displayCheckbox ? <Checkbox checked={selected} /> : null}
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                className={cn(styles.input, inputClass)}
                label={hideLabel ? undefined : label}
                InputLabelProps={
                  hideLabel
                    ? {
                        className: 'fs-unmask',
                        shrink: false,
                      }
                    : {
                        className: 'fs-unmask',
                        required: Boolean(required),
                        shrink: Boolean(value.length) || undefined,
                        sx: {
                          fontSize: '14px',
                          color: 'rgba(23, 36, 51, 0.6)',
                          backgroundColor: 'white',
                          px: 0.3,
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(6px, -10px) scale(0.75)',
                          },
                          '& .MuiFormLabel-asterisk': {
                            color: '#d32f2f',
                            opacity: 1,
                            fontWeight: 700,
                          },
                        },
                      }
                }
                sx={{
                  '& .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                    transition: 'max-width 0.2s ease-out',
                  },
                  '& .MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                  },
                  '&&&& .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                  },
                }}
                error={!!errorMessage}
                helperText={errorMessage}
                inputProps={{
                  ...params.inputProps,
                  'aria-required': required || undefined,
                  'data-testid': `multi-select-input-text-${name}`,
                  className: cn(params.inputProps.className, 'fs-exclude'),
                }}
                {...(register ? register(name, registerOptions) : {})}
              />
            )}
          />
        </FormControl>
      </Box>
    );
  },
);

export default MultiSelectInputMUI;
